import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideRouter, withComponentInputBinding } from '@angular/router';

import {
  AuthEffects,
  authFeatureKey,
  authFeatureReducer,
  authTokenInterceptor,
  provideAuth,
  refreshTokenInterceptor,
  sessionIdInterceptor
} from '@asset-solutions/customer-portal/auth/data-access';
import { CUSTOMER_PORTAL_CONFIG, type ConfigInterface, FIREBASE_APP } from '@asset-solutions/customer-portal/config';
import { OrdersEffects, ordersFeatureKey, ordersFeatureReducer } from '@asset-solutions/customer-portal/orders/store';
import { OPEN_WEATHER_CONFIG } from '@asset-solutions/shared/config';
import { API_CONFIG, loadingInterceptorFn } from '@asset-solutions/shared/http';
import { loggingInterceptorFn, provideLogger } from '@asset-solutions/shared/logging';
import { provideColorMode } from '@asset-solutions/shared/ui';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { initializeApp } from 'firebase/app';

import { appRoutes } from './app.routes';
import { ErrorHandlerService } from './logging/error-handler';
import { CustomerPortalMetadataService } from './logging/metadata.service';
import { CustomerPortalServerLogger } from './logging/server-logger.service';

export const getApplicationConfig = (config: ConfigInterface): ApplicationConfig => {
  const firebaseApp = initializeApp(config.firebase);
  return {
    providers: [
      // Providers from legacy modules
      importProvidersFrom(MatNativeDateModule, MatSnackBarModule, MatDialogModule),

      // Angular providers
      provideHttpClient(
        withInterceptors([
          loggingInterceptorFn,
          loadingInterceptorFn,
          sessionIdInterceptor,
          refreshTokenInterceptor,
          authTokenInterceptor
        ])
      ),
      provideRouter(appRoutes, withComponentInputBinding()),

      // Third party providers
      provideStore(
        {
          [authFeatureKey]: authFeatureReducer,
          [ordersFeatureKey]: ordersFeatureReducer,
          router: routerReducer
        },
        {
          runtimeChecks: {
            strictActionSerializability: true,
            strictActionTypeUniqueness: true,
            strictStateSerializability: true
          }
        }
      ),
      provideEffects([AuthEffects, OrdersEffects]),
      provideRouterStore(),

      // Internal providers
      { provide: FIREBASE_APP, useValue: firebaseApp },
      provideAuth(firebaseApp),
      provideColorMode('customer-portal-color-mode'),
      provideLogger(config.logging, {
        customServerLogger: CustomerPortalServerLogger,
        customMetadataService: CustomerPortalMetadataService
      }),
      { provide: ErrorHandler, useClass: ErrorHandlerService },
      { provide: CUSTOMER_PORTAL_CONFIG, useValue: config },
      { provide: API_CONFIG, useValue: config.api },
      { provide: OPEN_WEATHER_CONFIG, useValue: config.weather }
    ]
  };
};
